import type { Problema } from '../types/api';

/**
 * Cliente HTTP del front. La sesión es la cookie httpOnly del BFF: aquí no hay tokens.
 * - 401 → al login de Zitadel (/auth/login) y vuelta a la página actual.
 * - Las peticiones que cambian algo llevan X-CSRF-Token (el de GET /api/me).
 */
const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly errores: Record<string, string>;

  constructor(p: Problema) {
    super(p.detail ?? p.title ?? `Error ${p.status}`);
    this.status = p.status;
    this.errores = p.errores ?? {};
  }
}

let csrf: string | null = null;

export function fijarCsrf(token: string | null): void {
  csrf = token;
}

export function urlLogin(volver: string = window.location.pathname + window.location.search): string {
  return `${BASE}/auth/login?return_to=${encodeURIComponent(volver)}`;
}

export const navegador = {
  ir(url: string): void {
    window.location.assign(url);
  },
};

async function peticion<T>(metodo: string, ruta: string, cuerpo?: unknown): Promise<T> {
  const cabeceras: Record<string, string> = { Accept: 'application/json' };
  if (cuerpo !== undefined) cabeceras['Content-Type'] = 'application/json';
  if (metodo !== 'GET' && csrf) cabeceras['X-CSRF-Token'] = csrf;

  const r = await fetch(BASE + ruta, {
    method: metodo,
    headers: cabeceras,
    credentials: 'include',
    body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
  });

  if (r.status === 401) {
    navegador.ir(urlLogin());
    throw new ApiError({ status: 401, detail: 'La sesión ha caducado: redirigiendo al login…' });
  }
  if (r.status === 204) return undefined as T;
  let datos: unknown = null;
  try {
    datos = await r.json();
  } catch {
    datos = null;
  }
  if (!r.ok) {
    const p = (datos ?? {}) as Partial<Problema>;
    throw new ApiError({ ...p, status: r.status, detail: p.detail ?? 'Error del servidor, inténtalo de nuevo.' });
  }
  return datos as T;
}

export const api = {
  get: <T>(ruta: string) => peticion<T>('GET', ruta),
  post: <T>(ruta: string, cuerpo?: unknown) => peticion<T>('POST', ruta, cuerpo ?? {}),
  put: <T>(ruta: string, cuerpo: unknown) => peticion<T>('PUT', ruta, cuerpo),
  del: <T>(ruta: string, cuerpo?: unknown) => peticion<T>('DELETE', ruta, cuerpo ?? {}),
};
